/**
 * adminNewsStore — News slice of the admin store (Zustand).
 * Exposes the news endpoints (`/api/admin/news`) so the pages never call the
 * API directly.
 *
 * Endpoints:
 *   GET    /api/admin/news                        — list (page, limit, search, status, featured, sort)
 *   GET    /api/admin/news/:id                    — detail
 *   POST   /api/admin/news                        — create
 *   PATCH  /api/admin/news/:id                    — update
 *   DELETE /api/admin/news/:id                    — delete
 *   PATCH  /api/admin/news/:id/publish            — publish
 *   PATCH  /api/admin/news/:id/unpublish          — unpublish
 *   PATCH  /api/admin/news/:id/feature            — feature
 *   PATCH  /api/admin/news/:id/unfeature          — unfeature
 *   GET    /api/admin/news/categories             — list categories
 *   POST   /api/admin/news/categories             — create category
 *   PATCH  /api/admin/news/categories/:id         — update category
 *   DELETE /api/admin/news/categories/:id         — delete category
 */
import { API_URL } from "../../constants";
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const newsInitialState = {
  // List
  news: [],
  newsLoading: false,
  newsError: null,
  newsTotal: 0,
  newsPages: 1,

  // Single article
  newsDetail: null,
  newsDetailLoading: false,
  newsDetailError: null,

  // Categories
  newsCategories: [],
  newsCategoriesLoading: false,
  newsCategoriesError: null,

  // Mutations (create / update / delete / publish / feature)
  newsMutationLoading: false,
  newsMutationError: null,
};

/** Normalize the news list payload (many shapes) into `{ items, total, totalPages }`. */
function normalizeNewsList(data) {
  if (!data || typeof data !== "object") return { items: [], total: 0, totalPages: 1 };

  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data.news)) {
    items = data.news;
  } else if (Array.isArray(data.items)) {
    items = data.items;
  } else if (Array.isArray(data.data)) {
    items = data.data;
  }

  const pagination = data.pagination || {};
  return {
    items: items.map(normalizeNewsItem),
    total: pagination.total ?? data.total ?? items.length ?? 0,
    totalPages: pagination.totalPages ?? pagination.pages ?? data.totalPages ?? data.pages ?? 1,
  };
}

/** Normalize the categories payload into an array. */
function normalizeCategories(data) {
  if (!data) return [];
  const raw = Array.isArray(data)
    ? data
    : data.categories || data.items || data.data || [];
  return Array.isArray(raw) ? raw : [];
}

/** Normalize a single news record's alternative key spellings. */
function normalizeNewsItem(n) {
  if (!n || typeof n !== "object") return n;
  return {
    ...n,
    id: n.id ?? n._id,
    title: n.title ?? "",
    content: n.content ?? "",
    categoryId:
      n.categoryId ??
      n.category_id ??
      (n.category && typeof n.category === "object" ? n.category.id : n.category) ??
      null,
    excerpt: n.excerpt ?? "",
    coverImage: n.coverImage ?? n.cover_image ?? n.image ?? null,
    coverImageAlt: n.coverImageAlt ?? n.cover_image_alt ?? "",
    status: n.status ?? (n.isPublished ? "PUBLISHED" : "DRAFT"),
    tags: Array.isArray(n.tags) ? n.tags : [],
    featured: Boolean(n.featured),
    seoTitle: n.seoTitle ?? n.seo_title ?? "",
    seoDescription: n.seoDescription ?? n.seo_description ?? "",
    publishAt: n.publishAt ?? n.publish_at ?? null,
    createdAt: n.createdAt ?? n.created_at ?? null,
    updatedAt: n.updatedAt ?? n.updated_at ?? null,
  };
}

/** Authenticated JSON fetch that surfaces the API error envelope as an Error. */
async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson = contentType.includes("application/json") ||
      text.trim().startsWith("{") || text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`));
  }

  return data;
}

export function createNewsActions(set, get, basePath = "/api/admin/news") {
  /** Run a mutation, flip the shared flags and return `{ success, data?, error? }`. */
  async function mutate({ path, method, body, successMessage }) {
    set({ newsMutationLoading: true, newsMutationError: null });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({
        newsMutationLoading: false,
        newsMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const msg = toDisplayString(err.message, "Action failed");
      set({ newsMutationLoading: false, newsMutationError: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // GET /api/admin/news
    fetchNews: async (params = {}) => {
      set({ newsLoading: true, newsError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ""
          )
        ).toString();
        const data = await apiRequest(get, `${basePath}${query ? `?${query}` : ""}`);
        const { items, total, totalPages } = normalizeNewsList(data);
        set({ newsLoading: false, news: items, newsTotal: total, newsPages: totalPages });
        return { success: true, items, total, totalPages };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch news");
        set({ newsLoading: false, newsError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/admin/news/:id
    fetchNewsById: async (id) => {
      set({ newsDetailLoading: true, newsDetailError: null });
      try {
        const data = await apiRequest(get, `${basePath}/${id}`);
        const detail = normalizeNewsItem(data?.data ?? data?.news ?? data);
        set({ newsDetailLoading: false, newsDetail: detail });
        return { success: true, data: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch article");
        set({ newsDetailLoading: false, newsDetailError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/admin/news
    uploadNewsImage: async (file) => {
      try {
        if (!file || !file.type.startsWith("image/")) throw new Error("Choose an image file.");
        if (file.size > 10 * 1024 * 1024) throw new Error("Image must be 10 MB or smaller.");
        const headers = { "Content-Type": file.type, "x-filename": file.name };
        const token = resolveToken(get);
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch(`${API_URL}/api/uploads/image?folder=news`, {
          method: "POST", headers, body: file,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(toDisplayString(payload?.error ?? payload?.message, "Image upload failed."));
        const upload = payload?.data ?? payload;
        const url = upload.url ?? upload.secureUrl ?? upload.secure_url ?? upload.imageUrl ?? upload.image_url;
        if (!url) throw new Error("Upload succeeded but no image URL was returned.");
        return { success: true, url };
      } catch (error) {
        return { success: false, error: toDisplayString(error.message, "Image upload failed.") };
      }
    },

    createNews: (payload) =>
      mutate({ path: basePath, method: "POST", body: payload, successMessage: "News created successfully" }),

    // PATCH /api/admin/news/:id
    updateNews: (id, payload) =>
      mutate({ path: `${basePath}/${id}`, method: "PATCH", body: payload, successMessage: "News updated successfully" }),

    // DELETE /api/admin/news/:id
    deleteNewsById: (id) =>
      mutate({ path: `${basePath}/${id}`, method: "DELETE", successMessage: "News deleted" }),

    // PATCH /api/admin/news/:id/publish | unpublish | feature | unfeature
    publishNews: (id) =>
      mutate({ path: `${basePath}/${id}/publish`, method: "PATCH", body: undefined, successMessage: "News published" }),
    unpublishNews: (id) =>
      mutate({ path: `${basePath}/${id}/unpublish`, method: "PATCH", body: undefined, successMessage: "News unpublished" }),
    featureNews: (id) =>
      mutate({ path: `${basePath}/${id}/feature`, method: "PATCH", body: undefined, successMessage: "Marked as featured" }),
    unfeatureNews: (id) =>
      mutate({ path: `${basePath}/${id}/unfeature`, method: "PATCH", body: undefined, successMessage: "Removed from featured" }),

    // GET /api/admin/news/categories
    fetchNewsCategories: async () => {
      set({ newsCategoriesLoading: true, newsCategoriesError: null });
      try {
        const data = await apiRequest(get, `${basePath}/categories`);
        const categories = normalizeCategories(data);
        set({ newsCategoriesLoading: false, newsCategories: categories });
        return { success: true, categories };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch categories");
        set({ newsCategoriesLoading: false, newsCategoriesError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/admin/news/categories
    createNewsCategory: (payload) =>
      mutate({ path: `${basePath}/categories`, method: "POST", body: payload, successMessage: "Category created" }),

    // PATCH /api/admin/news/categories/:id
    updateNewsCategory: (id, payload) =>
      mutate({ path: `${basePath}/categories/${id}`, method: "PATCH", body: payload, successMessage: "Category updated" }),

    // DELETE /api/admin/news/categories/:id
    deleteNewsCategory: (id) =>
      mutate({ path: `${basePath}/categories/${id}`, method: "DELETE", successMessage: "Category deleted" }),
  };
}

const newsActions = createNewsActions;
export default newsActions;
